/**
 * Tool Import Dialog
 * 4-step workflow: Upload PDF → Parse pages → Review items → Create tools
 */

import { useState, useCallback, useRef } from 'react';
// DTC-K: route the import-tool `parsed_at` lifecycle write through the
// canonical clock so persisted import-state timestamps are
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';

import {
  FileUp,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Upload,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { computePurchasePrice } from '@/lib/computePurchasePrice';

// ── Types ──

interface ParsedLineItem {
  page_number: number;
  line_number: number;
  part_number: string | null;
  description: string;
  qty: number | null;
  unit_price: number | null;
  line_total: number | null;
  invoice_date: string | null;
  invoice_no: string | null;
  confidence: number;
  selected: boolean;
}

interface DocumentMeta {
  id: string;
  vendor: string | null;
  currency: string;
  original_filename: string;
}

type Step = 'upload' | 'parsing' | 'review' | 'creating';

interface ToolImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technicianProfileId: string;
}

// ── Helpers ──

async function renderPageToBase64(pdf: any, pageNum: number): Promise<string> {
  const page = await pdf.getPage(pageNum);
  // Scale 0.75 + JPEG 60% keeps text readable while drastically reducing payload
  // A typical scanned invoice page at 1.0/0.8 can be 1-3MB; at 0.75/0.6 it's ~200-500KB
  const viewport = page.getViewport({ scale: 0.75 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d')!;
  await page.render({ canvasContext: ctx, viewport }).promise;
  const base64 = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];
  // Clean up
  canvas.width = 0;
  canvas.height = 0;
  return base64;
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function callParseWithRetry(
  pages: { image: string; pageNumber: number }[],
  retries = 4
): Promise<any> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('parse-tool-invoice-pages', {
        body: { pages },
      });

      if (error) {
        const status = (error as any)?.status;
        // Retryable: 429 (rate limit), 502/503/504 (gateway errors), or generic fetch failures
        if (attempt < retries && (!status || status === 429 || status >= 500)) {
          const backoff = Math.pow(2, attempt) * 1500; // 1.5s, 3s, 6s, 12s
          console.log(`[import] Error (status=${status}) on attempt ${attempt + 1}, retrying in ${backoff}ms`);
          await delay(backoff);
          continue;
        }
        throw error;
      }

      // Check retryable flag in response body
      if (data?.retryable && attempt < retries) {
        const backoff = Math.pow(2, attempt) * 1500;
        console.log(`[import] Retryable response on attempt ${attempt + 1}, retrying in ${backoff}ms`);
        await delay(backoff);
        continue;
      }

      if (data?.error && !data?.retryable) {
        throw new Error(data.error);
      }

      return data;
    } catch (err: any) {
      // Catch network-level "Failed to fetch" errors
      if (attempt < retries && (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError') || err.name === 'TypeError')) {
        const backoff = Math.pow(2, attempt) * 2000; // 2s, 4s, 8s, 16s
        console.log(`[import] Network error on attempt ${attempt + 1}: ${err.message}, retrying in ${backoff}ms`);
        await delay(backoff);
        continue;
      }
      throw err;
    }
  }
  throw new Error('Max retries exceeded');
}

// ── Component ──

export function ToolImportDialog({ open, onOpenChange, technicianProfileId }: ToolImportDialogProps) {
  const queryClient = useQueryClient();

  // State
  const [step, setStep] = useState<Step>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [docMeta, setDocMeta] = useState<DocumentMeta | null>(null);
  const [lineItems, setLineItems] = useState<ParsedLineItem[]>([]);
  const [failedPages, setFailedPages] = useState<number[]>([]);
  const [parsingProgress, setParsingProgress] = useState({ current: 0, total: 0, itemsFound: 0 });
  const [isCreating, setIsCreating] = useState(false);
  const abortRef = useRef(false);

  // Reset on close
  const handleOpenChange = (o: boolean) => {
    if (!o) {
      abortRef.current = true;
      setStep('upload');
      setIsUploading(false);
      setDocMeta(null);
      setLineItems([]);
      setFailedPages([]);
      setParsingProgress({ current: 0, total: 0, itemsFound: 0 });
      setIsCreating(false);
    }
    onOpenChange(o);
  };

  // ── Step 1: Upload ──
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be under 20 MB');
      return;
    }

    setIsUploading(true);
    abortRef.current = false;

    try {
      // Create document record first to get ID
      const { data: doc, error: docErr } = await supabase
        .from('toolbox_import_documents')
        .insert({
          technician_profile_id: technicianProfileId,
          original_filename: file.name,
          storage_path: 'pending', // Will update after upload
          status: 'uploaded',
        })
        .select('id')
        .single();

      if (docErr) throw docErr;

      // Upload to storage
      const storagePath = `${technicianProfileId}/${doc.id}/${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('toolbox-imports')
        .upload(storagePath, file, { contentType: 'application/pdf', upsert: false });

      if (uploadErr) throw uploadErr;

      // Update storage path
      await supabase
        .from('toolbox_import_documents')
        .update({ storage_path: storagePath })
        .eq('id', doc.id);

      setDocMeta({
        id: doc.id,
        vendor: null,
        currency: 'USD',
        original_filename: file.name,
      });

      // Start parsing
      await startParsing(doc.id, file);
    } catch (err: any) {
      console.error('[import] Upload failed:', err);
      toast.error('Upload failed: ' + (err.message || 'Unknown error'));
      setIsUploading(false);
    }
  }, [technicianProfileId]);

  // ── Step 2: Parse ──
  const startParsing = async (docId: string, file: File) => {
    setStep('parsing');
    setIsUploading(false);

    try {
      // Load PDF with pdfjs-dist (same ESM pattern as ROUpload.tsx)
      const pdfjsLib: any = await import('pdfjs-dist/build/pdf.mjs');
      const worker = await import('pdfjs-dist/build/pdf.worker.mjs?url');
      pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdf.numPages;

      // Set page_count and status='parsing'
      await supabase
        .from('toolbox_import_documents')
        .update({ page_count: totalPages, status: 'parsing' })
        .eq('id', docId);

      setParsingProgress({ current: 0, total: totalPages, itemsFound: 0 });

      const allItems: ParsedLineItem[] = [];
      const failed: number[] = [];
      let globalLineNumber = 0;
      let vendor: string | null = null;
      let currency = 'USD';

      // Send 1 page per chunk to keep payload size manageable for scanned PDFs
      // Add inter-request delay to avoid rate limiting and connection exhaustion
      for (let i = 0; i < totalPages; i += 1) {
        if (abortRef.current) break;

        // Stagger requests: wait 800ms between pages to avoid overwhelming the edge function
        if (i > 0) await delay(800);

        const pageNums = [i + 1];

        try {
          // Render pages to base64
          const pages = await Promise.all(
            pageNums.map(async (pn) => ({
              image: await renderPageToBase64(pdf, pn),
              pageNumber: pn,
            }))
          );

          // Call edge function with retry
          const result = await callParseWithRetry(pages);

          // Capture document-level metadata from first successful chunk
          if (result.vendor && !vendor) vendor = result.vendor;
          if (result.currency) currency = result.currency;

          // Build line items
          const chunkItems: ParsedLineItem[] = (result.items || []).map((item: any, idx: number) => {
            globalLineNumber++;
            return {
              page_number: pageNums[0], // Assign to first page of chunk
              line_number: globalLineNumber,
              part_number: item.part_number,
              description: item.description,
              qty: item.qty,
              unit_price: item.unit_price,
              line_total: item.line_total,
              invoice_date: result.invoice_date,
              invoice_no: result.invoice_no,
              confidence: item.confidence ?? 0.5,
              selected: true,
            };
          });

          // Persist to DB (ON CONFLICT DO NOTHING via unique constraint)
          if (chunkItems.length > 0) {
            const dbRows = chunkItems.map((item) => ({
              document_id: docId,
              technician_profile_id: technicianProfileId,
              page_number: item.page_number,
              line_number: item.line_number,
              part_number: item.part_number,
              description: item.description,
              qty: item.qty,
              unit_price: item.unit_price,
              line_total: item.line_total,
              invoice_date: item.invoice_date,
              invoice_no: item.invoice_no,
              confidence: item.confidence,
            }));

            await supabase.from('toolbox_import_line_items').upsert(dbRows, {
              onConflict: 'document_id,page_number,line_number',
              ignoreDuplicates: true,
            });
          }

          allItems.push(...chunkItems);
        } catch (err) {
          console.error(`[import] Failed to parse pages ${pageNums.join(',')}:`, err);
          failed.push(...pageNums);
        }

        setParsingProgress({
          current: Math.min(i + 1, totalPages),
          total: totalPages,
          itemsFound: allItems.length,
        });
      }

      // Update document status
      const finalStatus = allItems.length > 0 ? 'parsed' : 'failed';
      await supabase
        .from('toolbox_import_documents')
        .update({
          status: finalStatus,
          vendor,
          currency,
          parsed_at: finalStatus === 'parsed' ? clock.now().toISOString() : null,
        })
        .eq('id', docId);

      setDocMeta((prev) => prev ? { ...prev, vendor, currency } : prev);
      setLineItems(allItems);
      setFailedPages(failed);

      if (allItems.length > 0) {
        setStep('review');
      } else {
        toast.error('No items could be extracted from this PDF');
        handleOpenChange(false);
      }
    } catch (err: any) {
      console.error('[import] Parsing failed:', err);
      toast.error('Parsing failed: ' + (err.message || 'Unknown error'));

      await supabase
        .from('toolbox_import_documents')
        .update({ status: 'failed' })
        .eq('id', docId);

      handleOpenChange(false);
    }
  };

  // ── Step 3: Review ──
  const toggleItem = (idx: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, selected: !item.selected } : item))
    );
  };

  const toggleAll = (checked: boolean) => {
    setLineItems((prev) => prev.map((item) => ({ ...item, selected: checked })));
  };

  const selectedCount = lineItems.filter((i) => i.selected).length;

  // ── Step 4: Create Tools ──
  const handleCreateTools = async () => {
    if (isCreating || !docMeta) return;
    setIsCreating(true);
    setStep('creating');

    try {
      const selected = lineItems.filter((i) => i.selected);
      const toolRows = selected.map((item) => ({
        technician_profile_id: technicianProfileId,
        // Core-100 R2 (CORE-K): an imported tool cites the purchase document it came from (evidence on file,
        // not verification — the database accepts only the owner's own uploaded document).
        source_document_id: docMeta.id,
        // Core-100 R4 (CORE-K): the document line this tool came from — one tool per (document, line).
        source_line_no: item.line_number,
        name: item.description,
        category: 'other' as const,
        part_number: item.part_number || null,
        purchase_price: computePurchasePrice(item.unit_price, item.line_total, item.qty),
        purchase_date: item.invoice_date || null,
        notes: [
          `Imported from ${docMeta.original_filename}`,
          item.invoice_no ? `Invoice #${item.invoice_no}` : null,
          docMeta.vendor ? `Vendor: ${docMeta.vendor}` : null,
        ]
          .filter(Boolean)
          .join(', '),
      }));

      // Core-100 R4 (CORE-K): idempotent per document line. A double-click, a retried request or a re-import of the
      // same document never duplicates a tool: the database holds one tool per (source_document_id, source_line_no)
      // and a line already imported is skipped (ON CONFLICT DO NOTHING).
      const { error } = await supabase
        .from('technician_tools')
        .upsert(toolRows as never, { onConflict: 'source_document_id,source_line_no', ignoreDuplicates: true });
      if (error) throw error;

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: ['toolbox-tools', technicianProfileId] });

      toast.success(`${toolRows.length} tool${toolRows.length > 1 ? 's' : ''} added to your toolbox`);
      handleOpenChange(false);
    } catch (err: any) {
      console.error('[import] Create tools failed:', err);
      toast.error('Failed to create tools: ' + (err.message || 'Unknown error'));
      setIsCreating(false);
      setStep('review');
    }
  };

  // ── Confidence dot ──
  const ConfidenceDot = ({ value }: { value: number }) => {
    const color =
      value >= 0.8 ? 'bg-green-500' : value >= 0.5 ? 'bg-yellow-500' : 'bg-red-500';
    return <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} title={`${Math.round(value * 100)}%`} />;
  };

  // ── Render ──
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            Import Tools from PDF Invoice
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a scanned invoice or receipt to extract tool line items.'}
            {step === 'parsing' && 'Analyzing your invoice pages...'}
            {step === 'review' && 'Review extracted items and select which ones to add.'}
            {step === 'creating' && 'Creating tools...'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            {isUploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Uploading PDF...</p>
              </div>
            ) : (
              <>
                <div className="border-2 border-dashed rounded-lg p-8 w-full text-center hover:border-primary/50 transition-colors">
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Select a PDF invoice (up to 20 MB)
                  </p>
                  <Input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileUpload}
                    className="max-w-xs mx-auto"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2: Parsing */}
        {step === 'parsing' && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="w-full max-w-sm space-y-2">
              <Progress
                value={
                  parsingProgress.total > 0
                    ? (parsingProgress.current / parsingProgress.total) * 100
                    : 0
                }
              />
              <p className="text-sm text-center text-muted-foreground">
                Parsing page {parsingProgress.current} of {parsingProgress.total}...
                {parsingProgress.itemsFound > 0 && (
                  <span className="font-medium"> ({parsingProgress.itemsFound} items found)</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && (
          <div className="flex flex-col gap-3 min-h-0 flex-1">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2">
              {docMeta?.vendor && (
                <Badge variant="secondary">
                  <FileText className="h-3 w-3 mr-1" />
                  {docMeta.vendor}
                </Badge>
              )}
              {lineItems[0]?.invoice_no && (
                <Badge variant="secondary">#{lineItems[0].invoice_no}</Badge>
              )}
              {lineItems[0]?.invoice_date && (
                <Badge variant="secondary">{lineItems[0].invoice_date}</Badge>
              )}
              <Badge variant="secondary">{docMeta?.currency || 'USD'}</Badge>
              <Badge variant="outline">{lineItems.length} items</Badge>
              {failedPages.length > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {failedPages.length} page{failedPages.length > 1 ? 's' : ''} failed
                </Badge>
              )}
            </div>

            {/* Select all */}
            <div className="flex items-center gap-2 px-1">
              <Checkbox
                checked={selectedCount === lineItems.length}
                onCheckedChange={(checked) => toggleAll(!!checked)}
                id="select-all"
              />
              <label htmlFor="select-all" className="text-xs text-muted-foreground cursor-pointer">
                {selectedCount === lineItems.length ? 'Deselect all' : 'Select all'} ({selectedCount} selected)
              </label>
            </div>

            {/* Items table */}
            <div className="flex-1 min-h-0 max-h-[50vh] overflow-auto border rounded-md">
              <div className="min-w-[500px]">
                {/* Header */}
                <div className="grid grid-cols-[28px_80px_1fr_50px_70px_70px_28px] gap-2 px-3 py-2 border-b bg-muted/50 text-xs font-medium text-muted-foreground sticky top-0">
                  <span />
                  <span>Part #</span>
                  <span>Description</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Unit $</span>
                  <span className="text-right">Total</span>
                  <span />
                </div>
                {/* Rows */}
                {lineItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[28px_80px_1fr_50px_70px_70px_28px] gap-2 px-3 py-2 border-b last:border-0 items-center text-sm hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleItem(idx)}
                    />
                    <span className="truncate text-xs text-muted-foreground">{item.part_number || '—'}</span>
                    <span className="truncate">{item.description}</span>
                    <span className="text-right text-muted-foreground">{item.qty ?? '—'}</span>
                    <span className="text-right text-muted-foreground">
                      {item.unit_price != null ? `$${item.unit_price.toFixed(2)}` : '—'}
                    </span>
                    <span className="text-right">
                      {item.line_total != null ? `$${item.line_total.toFixed(2)}` : '—'}
                    </span>
                    <ConfidenceDot value={item.confidence} />
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTools} disabled={selectedCount === 0}>
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Create {selectedCount} Tool{selectedCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Creating */}
        {step === 'creating' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Creating tools...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
